import React, { useState } from 'react';
import {
    Box,
    Button,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

import BlockHeader from '../../common/BlockHeader';
import BlockSubHeader from '../../common/BlockSubHeader';
import ContentContainer from '../../common/ContentContainer';
import { useDataState } from '../../../providers/DataProvider.hooks';
import { getPositionImage, getPositionMedal } from '../../../utils/helpers';

import rewards_small from '../../../assets/common/rewards_small.svg';
import clans_score from '../../../assets/common/clans_score.svg';
import medals from '../../../assets/common/medals.svg';
import question_mark from '../../../assets/common/question_mark.svg';
import { Modal } from './Modal';
import { useNavigate } from 'react-router-dom';
import { AppTooltip } from '../../common/Tooltip';

export const ClanScoreboard = () => {
    const { clans, setSelectedClan } = useDataState();
    const [openModal, setOpenModal] = useState(false);
    const navigate = useNavigate();

    return (
        <ContentContainer>
            <Box display={'flex'} justifyContent={'space-between'}>
                <Box>
                    <BlockHeader text="Clan Mastery Scoreboard"></BlockHeader>
                    <BlockSubHeader
                        text="The last update of the tournament data was"
                        time={clans?.last_modified}
                    ></BlockSubHeader>
                </Box>
                <Box>
                    <Button
                        startIcon={<img src={question_mark} alt="How do we count score" />}
                        sx={{
                            marginTop: '5px',
                            borderRadius: '32px',
                            border: '1px solid #D8DFE5',
                            color: '#0F0E0E',
                            fontFamily: 'AvenirNext500',
                            fontSize: '16px',
                            lineHeight: 'normal',
                            padding: '20px 30px',
                            textTransform: 'none',
                            transition: 'all ease 0.3s',

                            '&:hover': {
                                backgroundColor: 'transparent',
                                border: '1px solid #C6CFD8',
                            },
                        }}
                        onClick={() => {
                            setOpenModal(true);
                        }}
                    >
                        How do we count score?
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    minHeight: '638px',
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    marginBottom: '60px',
                    overflow: 'hidden',
                }}
            >
                {clans?.data ? (
                    <TableContainer
                        sx={{
                            margin: '0 10px 10px',
                        }}
                    >
                        <Table
                            sx={{
                                '& th': {
                                    borderBottom: 0,
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow
                                    sx={{
                                        '& th': {
                                            color: '#0F0E0E',
                                            fontFamily: 'AvenirNext600',
                                            fontSize: '20px',
                                            height: '88px',
                                            padding: '0 30px',
                                        },
                                    }}
                                >
                                    <TableCell align="center">
                                        <img src={rewards_small} alt="rewards"></img>
                                    </TableCell>
                                    <TableCell colSpan={2} sx={{ borderLeft: '1px solid #ECF0F4' }}>
                                        <Box display={'flex'} gap={'10px'}>
                                            <img src={clans_score} alt="clans score"></img>Clans tournament score
                                        </Box>
                                    </TableCell>
                                    <TableCell colSpan={3} align="center" sx={{ borderLeft: '1px solid #ECF0F4' }}>
                                        <Box
                                            display={'flex'}
                                            gap={'10px'}
                                            alignItems={'center'}
                                            justifyContent={'center'}
                                        >
                                            <img src={medals} alt="medals"></img>
                                            Leaderboards positions
                                            <AppTooltip
                                                title={
                                                    <Box
                                                        sx={{
                                                            padding: '30px',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: '#0F0E0E',
                                                                fontFamily: 'AvenirNext400',
                                                                fontSize: '16px',
                                                                lineHeight: 'normal',
                                                            }}
                                                        >
                                                            The clan's Mastery Score depends on its performance in all
                                                            the three Purr-formance Charts.
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                color: '#0F0E0E',
                                                                fontFamily: 'AvenirNext600',
                                                                fontSize: '16px',
                                                                marginTop: '20px',
                                                            }}
                                                        >
                                                            The better the positions in these charts, the higher the
                                                            clan's overall score.
                                                        </Typography>
                                                    </Box>
                                                }
                                            ></AppTooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                <TableRow
                                    sx={{
                                        background: '#F6F9FC',
                                        '& th': {
                                            color: '#9298A1',
                                            fontFamily: 'AvenirNext400',
                                            fontSize: '14px',
                                            height: '60px',
                                            padding: 0,
                                        },
                                    }}
                                >
                                    <TableCell align="center">Position</TableCell>
                                    <TableCell
                                        sx={{
                                            borderLeft: '1px solid #ECF0F4',
                                            paddingLeft: '30px !important',
                                        }}
                                    >
                                        Clan
                                    </TableCell>
                                    <TableCell align="center">Tournament score</TableCell>
                                    <TableCell align="center" width={'15%'} sx={{ borderLeft: '1px solid #ECF0F4' }}>
                                        Clash wins
                                    </TableCell>
                                    <TableCell width={'15%'} align="center">
                                        Highest win rate
                                    </TableCell>
                                    <TableCell width={'15%'} align="center">
                                        WLKN Gained
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clans.data
                                    .sort((a, b) => a.rank - b.rank)
                                    .map((clan, index) => (
                                        <TableRow
                                            key={clan.clan}
                                            sx={{
                                                '& td': {
                                                    borderBottom: 0,
                                                },
                                                '&:nth-of-type(even) td': {
                                                    backgroundColor: '#F6F9FC',
                                                },
                                                '&:hover': {
                                                    cursor: 'pointer',
                                                },
                                            }}
                                            onClick={() => {
                                                setSelectedClan(clan);
                                                navigate('/' + clan.clan);
                                            }}
                                        >
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    color: '#9298A1',
                                                    fontFamily: 'AvenirNext400',
                                                    fontSize: '16px',
                                                    lineHeight: 'normal',
                                                    height: '96px',
                                                    padding: 0,
                                                }}
                                            >
                                                {getPositionImage(clan.scoreboardPosition)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    position: 'relative',
                                                    color: '#0F0E0E',
                                                    fontFamily: 'AvenirNext500',
                                                    fontSize: '16px',
                                                    '&: after': {
                                                        content: '""',
                                                        background: '#ECF0F4',
                                                        position: 'absolute',
                                                        bottom: '20px',
                                                        left: 0,
                                                        height: 'calc(100% - 40px)',
                                                        width: '1px',
                                                    },
                                                }}
                                            >
                                                <Box display={'flex'} gap={'10px'} alignItems={'center'}>
                                                    <Box
                                                        sx={{
                                                            height: '48px',
                                                            minWidth: '48px',
                                                            borderRadius: '50%',
                                                            border: clan.picture ? 'none' : '1px solid lightgrey',
                                                            display: 'inline-block',
                                                            overflow: 'hidden',
                                                            background: `url(${clan.picture})`,
                                                            backgroundSize: 'cover',
                                                            backgroundPosition: 'center',
                                                        }}
                                                    ></Box>
                                                    {clan.clan}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box
                                                    sx={{
                                                        border: '1px solid #B2B7BE4D',
                                                        padding: '10px 30px',
                                                        borderRadius: '21px',
                                                        fontFamily: 'AvenirNext600',
                                                        fontSize: '16px',
                                                        width: 'max-content',
                                                        margin: 'auto',
                                                    }}
                                                >
                                                    {(Math.trunc((clans.data.length - clan.rank) * 100) / 100).toFixed(
                                                        2,
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: 'relative',
                                                    '&: after': {
                                                        content: '""',
                                                        background: '#ECF0F4',
                                                        position: 'absolute',
                                                        bottom: '20px',
                                                        left: 0,
                                                        height: 'calc(100% - 40px)',
                                                        width: '1px',
                                                    },
                                                }}
                                            >
                                                {getPositionMedal(clan.rank_win)}
                                            </TableCell>
                                            <TableCell align="center">{getPositionMedal(clan.rank_winrate)}</TableCell>
                                            <TableCell align="center">{getPositionMedal(clan.rank_wlkn)}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Skeleton variant="rounded" width={'100%'} height={'100%'} />
                )}
            </Box>
            <Modal openModal={openModal} setOpenModal={setOpenModal}></Modal>
        </ContentContainer>
    );
};
